import { html, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { pdsCustomElement as customElement } from '../../decorators/pds-custom-element';
import { PdsElement } from '../PdsElement';
import styles from './secondary-navigation-level-two.scss?inline';
import '../heading/heading';
import '../link/link';
import '../list/list';
import '../list-item/list-item';
import '../collapsible/collapsible';

/**
 * @summary This component generates the level two navigation within the secondary navigation, and generates a heading over slotted list contents at desktop and either a collapsible or a link (depending on props) at mobile.
 *
 * @slot default A list of link items
 */
@customElement('pds-secondary-navigation-level-two', {
  category: 'component',
  type: 'component',
  styles,
})
export class PdsSecondaryNavigationLevelTwo extends PdsElement {
  /**
   * The title of the top tier item (whether a parent or link)
   */
  @property({ type: String, reflect: true })
  title: string = '';

  /**
   * If the top tier item is a link, the href for it
   */
  @property({ reflect: true })
  href: string;

  /**
   * Set to true for visual representation of the user's current page
   * If not set, component logic will determine the value by matching the window location's href to the component's href property
   */
  @property({ reflect: true })
  activePage: boolean = false;

  /**
   * @internal
   * Function to determine if this is the active page
   * This function looks at the url of each link item
   * and determines if the current URL matches the nav item URL
   * This is the default behavior of the activePageCallback prop
   *
   */
  determineActivePage() {
    let returnValue = false;
    if (this.href) {
      if (window.location.href === this.href) {
        returnValue = true;
      }
    }

    return returnValue;
  }

  /**
   * @internal
   * Callback function to determine if this is the active page
   * It MUST return a boolean
   * Defaults to looking at the url of each secondary nav level two item
   * and determines if the current URL matches the nav item URL
   */
  @property()
  activePageCallback: Function = this.determineActivePage;

  /**
   * @internal
   */
  firstUpdated(): void {
    this.activePage = this.activePageCallback();
  }

  /**
   * @internal
   * Returns the markup to be used at mobile breakpoints
   */
  generateNavTwoItemMarkupMobile() {
    if (!this.href) {
      return html`<pds-collapsible
        variant="secondary-nav"
        class="${this.classEl('menu-item')}"
        ><span slot="summary-title">${this.title}</span
        ><span slot="collapsible-content"
          >${this.getDynamicSlotMarkup()}</span
        ></pds-collapsible
      >`;
    }
    return html`<pds-link
      class="${this.classEl('menu-item')} ${this.activePage
        ? this.classMod('active-page')
        : nothing}"
      ariaCurrent="${this.activePage ? 'page' : nothing}"
      >${this.title}</pds-link
    >`;
  }

  /**
   * @internal
   * Returns the markup to be used at large breakpoints
   */
  generateNavTwoItemMarkupDesktop() {
    if (!this.href) {
      return html`<div class="${this.classEl('menu-item')}">
        <span class="${this.classEl('category-title')}">${this.title}</span>
        ${this.getDynamicSlotMarkup()}
      </div>`;
    }

    return html`<pds-link
      class="${this.classEl('menu-item')} ${this.classMod(
        'menu-item-link',
      )} ${this.activePage ? this.classMod('active-page') : nothing}"
      href="${this.href}"
      ariaCurrent="${this.activePage ? 'page' : nothing}"
      >${this.title}</pds-link
    >`;
  }

  render() {
    return html`<div class=${this.getClass()}>
      <slot
        hidden
        @slotchange="${(e: Event) =>
          this.duplicateSlotMarkup(e.target as HTMLSlotElement)}"
        class="${this.classEl('slot')}"
      ></slot>
      <div class="${this.classMod('mobile')}">
        ${this.generateNavTwoItemMarkupMobile()}
      </div>
      <div class="${this.classMod('desktop')}">
        ${this.generateNavTwoItemMarkupDesktop()}
      </div>
    </div>`;
  }
}
